import { useEffect } from "react";
import { useRouteError } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCw, ArrowLeft, AlertTriangle } from "lucide-react";
import { isChunkLoadError } from "@/shared/lib/chunk-reload";
import {
  isEmbedded,
  postClose,
  postError,
} from "@/shared/lib/host-lifecycle";

/**
 * What the vendor sees when a route throws.
 *
 * Without an `errorElement` React Router renders its own developer screen
 * ("Unexpected Application Error!", a stack, and advice addressed to the
 * developer) — inside the dashboard's iframe, to a merchant, mid-edit. This
 * replaces it with something that says what happened and what to do.
 *
 * Two cases, because they deserve different words:
 *   · a stale lazy chunk — the ordinary consequence of a deploy landing while
 *     a tab was open. Nothing is broken and a reload genuinely fixes it, so
 *     say that rather than apologising for a fault the vendor didn't hit.
 *   · anything else — an honest "something went wrong", with the same
 *     recovery plus a way back out.
 *
 * The technical detail is DEV-only: it is what a developer needs and pure
 * noise (and a small information leak) for a merchant.
 */
export function RouteErrorBoundary({
  // Injectable for the same reason installChunkReloadHandler injects its own
  // reload: jsdom's window.location is unforgeable — its methods are
  // read-only AND non-configurable, so a test cannot observe them any other
  // way. Both default to the real thing.
  reload = () => window.location.reload(),
  navigate = (path: string) => window.location.assign(path),
}: {
  reload?: () => void;
  navigate?: (path: string) => void;
} = {}) {
  const { t } = useTranslation();
  const error = useRouteError();
  const staleBuild = isChunkLoadError(error);

  const detail =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : null;

  /**
   * Tell the host the surface is down.
   *
   * The host had nothing to go on before this: with its legacy template
   * routes removed there is nothing behind the Studio, so a frame showing
   * this screen was indistinguishable from a working one (host team,
   * 2026-08-13). It can now hold its loader, show its own error state and
   * offer a retry that reloads the frame.
   *
   * Posted on mount, not from the Back button — see `goBack` below, which is
   * a deliberate exit and stays a close. `detail` rides along for the host's
   * logs only; every word the merchant reads is theirs, not ours.
   */
  useEffect(() => {
    postError("RENDER_ERROR", detail ?? undefined);
  }, [detail]);

  /**
   * Say what actually happened, in DEV.
   *
   * This screen is the end of the line for a merchant and, until now, for
   * whoever has to fix it: the host renders its own copy, `detail` rides the
   * message to the host and is displayed nowhere, and this boundary logged
   * nothing. React's own uncaught-error log is the only trace, and inside an
   * iframe it is easy to miss among the host's. One line, with the error
   * object, so the console names the throw and its stack.
   */
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    // eslint-disable-next-line no-console
    console.error(
      "[studio] route error — this is what the host reports as RENDER_ERROR:",
      error,
    );
  }, [error]);

  // Embedded, "back" belongs to the HOST — the same contract the editor's own
  // back button follows. An in-frame navigation would strand the vendor on a
  // gallery rendered inside the editor's slot.
  //
  // Still a CLOSE, deliberately. The host's brief said to replace this call
  // with the error post, but this one is not the automatic reaction to the
  // failure — it is the merchant clicking "Back", which means exactly what
  // close means. Replacing it would leave them on a dead surface with a
  // button that no longer goes anywhere.
  const goBack = () => {
    if (isEmbedded()) {
      postClose();
      return;
    }
    navigate("/templates");
  };

  return (
    <div
      data-cy="studio-route-error"
      role="alert"
      className="flex min-h-screen items-center justify-center px-6 py-12"
    >
      <div className="w-full max-w-md text-center">
        <span
          className="mx-auto mb-5 inline-flex h-11 w-11 items-center justify-center rounded-full"
          style={{ background: "var(--color-danger-bg, rgba(220,38,38,0.08))" }}
        >
          {/* Danger is the system's colour for a failed state — the one place
              a semantic colour is doing its actual job. */}
          <AlertTriangle
            className="h-5 w-5"
            style={{ color: "var(--color-danger)" }}
            aria-hidden="true"
          />
        </span>

        <h1 className="m-0 mb-2 text-xl font-semibold tracking-tight text-primary">
          {staleBuild
            ? t("templates.studio.routeError.staleTitle")
            : t("templates.studio.routeError.title")}
        </h1>
        <p className="m-0 text-sm leading-relaxed text-secondary">
          {staleBuild
            ? t("templates.studio.routeError.staleBody")
            : t("templates.studio.routeError.body")}
        </p>

        <div className="mt-6 flex flex-col-reverse items-stretch justify-center gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={goBack}
            data-cy="studio-route-error-back"
            className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-4 text-sm font-medium text-secondary transition-colors hover:bg-surface hover:text-primary dark:border-[#363b3d]"
          >
            <ArrowLeft className="h-4 w-4" />
            {isEmbedded()
              ? t("templates.studio.routeError.backToDashboard")
              : t("templates.studio.routeError.backToTemplates")}
          </button>
          <button
            type="button"
            onClick={reload}
            data-cy="studio-route-error-reload"
            className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            <RefreshCw className="h-4 w-4" />
            {t("templates.studio.routeError.reload")}
          </button>
        </div>

        {import.meta.env.DEV && detail && (
          <pre
            data-cy="studio-route-error-detail"
            className="mt-6 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-surface p-3 text-left text-[11px] leading-relaxed text-tertiary dark:border-[#363b3d]"
          >
            {detail}
          </pre>
        )}
      </div>
    </div>
  );
}
